import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FornecedoresService } from '../fornecedores.service';
import { TestemunhoService } from '../../testemunhos/testemunho.service';

@Component({
  standalone: false,
  selector: 'app-fornecedor-preview',
  template: `
    <div *ngIf="fornecedor; else notFound" class="max-w-4xl mx-auto bg-white">
      <!-- Header Section -->
      <div class="bg-white border-b p-6">
        <div class="flex items-start gap-4">
          <div class="w-24 h-24 rounded-full bg-surface flex items-center justify-center overflow-hidden border-2 border-primary">
            <img *ngIf="primaryImage" [src]="primaryImage.url" [alt]="fornecedor.nome" class="w-full h-full object-cover" />
            <span *ngIf="!primaryImage" class="text-4xl text-muted">📷</span>
          </div>
          <div class="flex-1">
            <div class="flex items-start justify-between">
              <div>
                <span class="inline-block px-3 py-1 bg-primary text-white text-xs rounded-full mb-2">{{ fornecedor.categoria?.nome || 'FOTOGRAFIA' }}</span>
                <h1 class="text-3xl font-serif font-bold text-gray-900">{{ fornecedor.nome }}</h1>
                <p class="text-gray-600 mt-1">{{ fornecedor.cidade || 'Piracicaba' }}, SP ★ {{ fornecedor.rating || 5 }}</p>
              </div>
            </div>
            <p class="mt-4 text-gray-700 leading-relaxed">{{ fornecedor.descricao }}</p>
          </div>
        </div>
      </div>

      <!-- Gallery Section -->
      <div class="p-6">
        <h3 class="text-xl font-semibold mb-4">Galeria de Fotos</h3>
        <div class="grid grid-cols-2 gap-4">
          <div *ngFor="let media of displayedMedias" class="aspect-video overflow-hidden rounded-lg border">
            <img [src]="media.url" [alt]="media.filename" class="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer" />
          </div>
        </div>
      </div>

      <!-- Testimonials Section -->
      <div class="bg-pink-50 p-6" *ngIf="testemunhos.length > 0">
        <h3 class="text-2xl font-serif font-bold mb-6">O que os noivos dizem</h3>
        <div class="space-y-4">
          <div *ngFor="let t of testemunhos" class="bg-white rounded-lg p-5 shadow-sm">
            <div class="flex items-start gap-3">
              <span class="text-4xl text-primary">"</span>
              <div class="flex-1">
                <p class="text-gray-700 italic leading-relaxed mb-3">{{ t.descricao }}</p>
                <p class="text-right text-primary font-semibold">— {{ t.nome }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- About Section -->
      <div class="p-6 border-t">
        <h3 class="text-xl font-semibold mb-4">Sobre</h3>
        <p class="text-gray-700 leading-relaxed whitespace-pre-line">{{ fornecedor.descricao }}</p>
      </div>

      <!-- Contact Section -->
      <div class="bg-white border-t p-6">
        <h3 class="text-xl font-semibold mb-4">Solicitar Orçamento</h3>
        <div class="space-y-4">
          <a *ngIf="fornecedor.whatsapp || fornecedor.telefone" 
             [href]="getWhatsAppLink()" 
             target="_blank"
             class="block w-full bg-green-500 text-white text-center py-3 rounded-lg font-medium hover:bg-green-600 transition">
            WhatsApp
          </a>
          <a *ngIf="fornecedor.website" 
             [href]="fornecedor.website" 
             target="_blank"
             class="block w-full border-2 border-gray-300 text-gray-700 text-center py-3 rounded-lg font-medium hover:bg-gray-50 transition">
            Visitar Site
          </a>
          <a *ngIf="fornecedor.instagram" 
             [href]="getInstagramLink()" 
             target="_blank"
             class="block w-full border-2 border-gray-300 text-gray-700 text-center py-3 rounded-lg font-medium hover:bg-gray-50 transition">
            Instagram
          </a>
        </div>

        <div class="mt-6 pt-6 border-t">
          <h4 class="font-semibold mb-3">Dados de Contato</h4>
          <div class="space-y-2 text-sm text-gray-600">
            <p *ngIf="fornecedor.telefone || fornecedor.whatsapp">
              <span class="font-medium">📞</span> {{ fornecedor.whatsapp || fornecedor.telefone }}
            </p>
            <p *ngIf="fornecedor.instagram">
              <span class="font-medium">📷</span> {{ '@' + getInstagramHandle() }}
            </p>
            <p *ngIf="fornecedor.email">
              <span class="font-medium">✉️</span> {{ fornecedor.email }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p class="mt-4 text-gray-600">Carregando...</p>
    </div>

    <ng-template #notFound>
      <div class="max-w-2xl mx-auto text-center py-16">
        <h3 class="text-xl font-semibold mb-2">Fornecedor não encontrado</h3>
        <p class="text-sm text-gray-600">Verifique se o cadastro existe ou tente novamente.</p>
      </div>
    </ng-template>
  `
})
export class FornecedorPreviewComponent implements OnInit {
  fornecedor: any;
  testemunhos: any[] = [];
  loading = true;
  displayedMedias: any[] = [];
  primaryImage: any;

  constructor(
    private route: ActivatedRoute,
    private fornecedoresSvc: FornecedoresService,
    private testemunhoSvc: TestemunhoService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadFornecedor(id);
      this.loadTestemunhos(id);
    }
  }

  loadFornecedor(id: string) {
    this.loading = true;
    this.fornecedoresSvc.adminGet(id).subscribe({
      next: (res: any) => {
        this.fornecedor = res;
        if (res?.medias?.length) {
          const medias = res.medias;
          this.displayedMedias = medias.slice(0, 4);
          this.primaryImage = medias.find((m: any) => m.isPrimary) || medias[0];
        } else {
          this.loadMedias(id);
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('[FornecedorPreview] erro ao carregar fornecedor', err);
        this.loading = false;
        this.fornecedor = null as any;
      }
    });
  }

  loadMedias(fornecedorId: string) {
    this.fornecedoresSvc.getMedia(fornecedorId).subscribe({
      next: (res: any) => {
        const medias = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
        this.displayedMedias = medias.slice(0, 4); // Mostrar até 4 imagens
        this.primaryImage = medias.find((m: any) => m.isPrimary) || medias[0];
      },
      error: (err) => {
        console.error('[FornecedorPreview] erro ao carregar mídias', err);
        this.displayedMedias = [];
      }
    });
  }

  loadTestemunhos(fornecedorId: string) {
    this.testemunhoSvc.listByFornecedor(fornecedorId, 1, 3).subscribe({
      next: (res: any) => {
        this.testemunhos = res?.data || [];
      },
      error: (err) => {
        console.error('[FornecedorPreview] erro ao carregar testemunhos', err);
        this.testemunhos = [];
      }
    });
  }

  getWhatsAppLink(): string {
    const phone = (this.fornecedor.whatsapp || this.fornecedor.telefone || '').replace(/\D/g, '');
    return `https://wa.me/${phone}`;
  }

  getInstagramLink(): string {
    const handle = this.getInstagramHandle();
    return `https://instagram.com/${handle}`;
  }

  getInstagramHandle(): string {
    if (!this.fornecedor.instagram) return '';
    return this.fornecedor.instagram.replace('@', '').replace('instagram.com/', '').replace('https://', '').replace('http://', '');
  }
}
